import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { DentalAsset } from './types';

/** Validate the decoder against independently registered source geometry.
 * NRRD native world units may need explicit scaling to canonical mm.
 * This changes physical metadata only, never voxel order or indices.
 */
export function normalizeAssetGrid(image: vtkImageData, asset: DentalAsset) {
  const { shapeIJK, ijkToLps: expected } = asset.frame;
  if (!shapeIJK || !expected || image.getDimensions().some((n, i) => n !== shapeIJK[i])) throw new Error('Decoded dimensions differ from the registered source grid.');
  const origin = [...image.indexToWorld([0, 0, 0])];
  const basis = [0, 1, 2].map(axis => [...image.indexToWorld([0, 1, 2].map(i => i === axis ? 1 : 0) as [number, number, number])].map((n, i) => n - origin[i]));
  const matches = (scale: number[]) => origin.every((n, row) => Math.abs(n * scale[row] - expected[row * 4 + 3]) <= 1e-3) &&
    basis.every((col, column) => col.every((n, row) => Math.abs(n * scale[row] - expected[row * 4 + column]) <= 1e-5));
  if (matches([1, 1, 1])) return;
  const scale = asset.worldUnitScale;
  if (!scale || !matches(scale)) throw new Error('Decoded affine differs from registered LPS millimetres; no resampling was applied.');
  const columns = basis.map(col => col.map((n, row) => n * scale[row]));
  const spacing = columns.map(col => Math.hypot(...col));
  image.setOrigin([origin[0] * scale[0], origin[1] * scale[1], origin[2] * scale[2]]);
  image.setSpacing([spacing[0], spacing[1], spacing[2]]);
  image.setDirection(new Float32Array(columns.flatMap((col, axis) => col.map(n => n / spacing[axis]))));
}
